import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listAdminQueue, listAdminPublishedOriginals } from "@/lib/admin.client";
import type { AdminDict } from "@/lib/admin-i18n";

type Tab = "queue" | "published";

export function Queue({ t, onOpen }: { t: AdminDict; onOpen: (id: string) => void }) {
  const [tab, setTab] = useState<Tab>("queue");

  const queueQ = useQuery({
    queryKey: ["admin", "queue"],
    queryFn: () => listAdminQueue(),
    enabled: tab === "queue",
  });
  const pubQ = useQuery({
    queryKey: ["admin", "published-originals"],
    queryFn: () => listAdminPublishedOriginals(),
    enabled: tab === "published",
  });

  const active = tab === "queue" ? queueQ : pubQ;
  const rows = (active.data ?? []) as any[];
  const heading = tab === "queue" ? t.queue : t.tab_published;
  const lead = tab === "queue" ? t.queue_oldest : t.published_lead;
  const emptyMsg = tab === "queue" ? t.queue_empty : t.published_empty;
  const dateCol = tab === "queue" ? t.col_submitted : t.col_published;

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-2xl" style={{ color: "var(--ink)" }}>{heading}</h2>
        <button
          onClick={() => active.refetch()}
          className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          {active.isFetching ? "…" : "↻"}
        </button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{lead}</p>

      <div className="mt-4 flex gap-1 border-b border-border text-xs uppercase tracking-wider">
        {(["queue", "published"] as Tab[]).map((k) => {
          const activeTab = tab === k;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              className="-mb-px border-b-2 px-3 py-2"
              style={{
                borderColor: activeTab ? "var(--ink)" : "transparent",
                color: activeTab ? "var(--ink)" : "var(--fog)",
              }}
            >
              {k === "queue" ? t.tab_queue : t.tab_published}
            </button>
          );
        })}
      </div>

      {active.isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">…</p>
      ) : active.error ? (
        <p className="mt-6 text-sm" style={{ color: "var(--rust)" }}>{(active.error as Error).message}</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 rounded-sm border border-dashed border-border p-6 text-sm italic text-muted-foreground">
          {emptyMsg}
        </p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3">{t.col_title}</th>
                <th className="py-2 pr-3">{t.col_language}</th>
                <th className="py-2 pr-3">{t.col_type}</th>
                <th className="py-2 pr-3">{t.col_status}</th>
                <th className="py-2 pr-3">{dateCol}</th>
                <th className="py-2 pr-3">{t.col_contact}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr
                  key={r.id}
                  onClick={() => onOpen(r.id)}
                  className="cursor-pointer border-b border-border/60 hover:bg-muted/40"
                >
                  <td className="py-2 pr-3">{r.title ?? <em className="text-muted-foreground">untitled</em>}</td>
                  <td className="py-2 pr-3">{r.language.toUpperCase()}</td>
                  <td className="py-2 pr-3">
                    {r.submission_type === "confessor" ? t.type_confessor : t.type_victim}
                  </td>
                  <td className="py-2 pr-3">
                    {r.status === "submitted" ? t.status_submitted
                      : r.status === "in_review" ? t.status_in_review
                      : r.status === "published" ? t.status_published
                      : t.status_needs_redaction}
                  </td>
                  <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                    {new Date((tab === "published" && r.published_at) ? r.published_at : r.created_at)
                      .toISOString().slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="text-xs" style={{ color: r.has_contact ? "var(--ink)" : "var(--fog)" }}>
                      {r.has_contact ? t.contact_yes : t.contact_no}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}